import { io } from 'socket.io-client'
import axios from 'axios'
import logger from '@/utils/logger'

let socket = null

function apiBaseWithoutPath() {
  const u = axios.defaults.baseURL || process.env.VUE_APP_API_URL || ''
  return String(u).replace(/\/api\/?$/i, '')
}

function bindBillNoteListener(target) {
  if (!target || target.__billNoteBound) return
  target.__billNoteBound = true
  target.on('bill-note:new', (payload) => {
    try {
      window.dispatchEvent(new CustomEvent('bill-note-new', { detail: payload }))
    } catch (_) {
      // ignore
    }
  })
}

function bindAccessRevokedListener(target) {
  if (!target || target.__accessRevokedBound) return
  target.__accessRevokedBound = true
  target.on('vendor-portal:access-revoked', (payload) => {
    try {
      window.dispatchEvent(
        new CustomEvent('vendor-portal-access-revoked', {
          detail: {
            code: payload?.code || 'PORTAL_ACCESS_REVOKED',
            message:
              payload?.message ||
              'Your vendor portal access was removed. You have been signed out.'
          }
        })
      )
    } catch (_) {
      // ignore
    }
  })
}

function bindVendorSocketListeners(target) {
  bindBillNoteListener(target)
  bindAccessRevokedListener(target)
}

/** Connect vendor portal to Socket.IO for live bill notes. */
export function connectVendorPortalSocket() {
  const token = localStorage.getItem('vendorToken')
  if (!token) return null
  if (socket?.connected) {
    bindVendorSocketListeners(socket)
    return socket
  }

  const url = apiBaseWithoutPath()
  if (!url) {
    logger.warn('vendorPortalSocket: no API base URL')
    return null
  }

  if (socket) {
    socket.auth = { token }
    bindVendorSocketListeners(socket)
    socket.connect()
    return socket
  }

  socket = io(url, {
    path: '/socket.io/',
    transports: ['websocket', 'polling'],
    auth: { token },
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 2000
  })

  bindVendorSocketListeners(socket)
  return socket
}

export function disconnectVendorPortalSocket() {
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
}

/** Ensure a single window-level handler for revoke force-logout. */
export function onVendorPortalAccessRevoked(handler) {
  if (typeof handler !== 'function') return () => {}
  const listener = (event) => handler(event?.detail || {})
  window.addEventListener('vendor-portal-access-revoked', listener)
  return () => {
    window.removeEventListener('vendor-portal-access-revoked', listener)
  }
}

export function isPortalAccessRevokedError(error) {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  if (status !== 403) return false
  if (data.code === 'PORTAL_ACCESS_REVOKED') return true
  const message = String(data.message || data.error || '').toLowerCase()
  return message.includes('revoked') || message.includes('access was removed')
}

export function portalAccessRevokedMessage(error) {
  return (
    error?.response?.data?.message ||
    'Your vendor portal access was removed. You have been signed out.'
  )
}
